import io from 'socket.io-client';
import feathers from '@feathersjs/client';
import WalletAuthenticationClient from './authentication/WalletAuthenticationClient';

const socket = io(process.env.REACT_APP_BACKEND_URL_SOCKETIO);
const auth = require('@feathersjs/authentication-client');

var alertIsShown = false;

class Feathers {
	constructor() {
		this.io = socket;
		this.init();
		this.ready = this.authenticateOnStart();
		alertIsShown = false;
	}

	init = () => {
		if (window.feathersClient === undefined) {
			console.log("Do feathers init")
			this.app = feathers();
			this.io = socket;

			window.feathersClient = this.app
				.configure(
					feathers.socketio(socket, {
						timeout: 2 * 60 * 1000, // 2 minutes
					})
				)
				.configure(
					auth({
						storage: window.sessionStorage,
						storageKey: 'jwt-accessToken',
						Authentication: WalletAuthenticationClient,
					})
				);
		} else {
			console.log("ignore feathers init");
		}
	};

	authenticateOnStart = async () => {
		try {
			await this.authenticate(); // uses stored token if present
			console.log('[Feathers] Initial authentication successful');
		} catch (err) {
			console.warn('[Feathers] Initial authentication skipped or failed:', err.message);
		}
	};

	// Wrap service methods to ensure token is valid
	withAuthGuard = (service) => {
		const methodsToWrap = ['find', 'get', 'create', 'update', 'patch', 'remove'];

		methodsToWrap.forEach(method => {
			const original = service[method];
			if (typeof original === 'function') {
				service[method] = async (...args) => {
					await this.ensureTokenIsValid();
					return original.apply(service, args);
				};
			}
		});

		return service;
	};

	// Check token expiry and re-authenticate if needed
	ensureTokenIsValid = async () => {
		await this.ready; // Wait for initial authentication to complete

		const authClient = this.authentication();

		const accessToken = await authClient.getAccessToken(); // sessionStorage.getItem('jwt-accessToken');
		if (!accessToken) {
			throw new Error('No access token found');
		}

		try {
			const payload = JSON.parse(atob(accessToken.split('.')[1]));
			const now = Date.now();
			const exp = payload.exp * 1000;

			// Re-authenticate if token expires in <30s
			if (exp - now < 30 * 1000) {
				console.log('[Feathers] Access token about to expire, refreshing...');
				await this.authenticate();
			}
		} catch (err) {
			console.error('Failed to decode or refresh token:', err);
			throw err;
		}
	};

	service = (name) => {
		const guardedService = this.withAuthGuard(window.feathersClient.service(name));

		const handler = {
			get(target, prop, receiver) {
				const original = target[prop];

				// Only wrap async methods like find/get/create/etc.
				if (typeof original === 'function') {
					return async (...args) => {
						try {
							return await original.apply(target, args);
						} catch (err) {
							if (err.message === 'No access token found' || err.message === 'Failed to decode or refresh token') {
								console.warn('[Feathers] Token/auth error intercepted in service()');
								if (!alertIsShown) {
									alert('Your session has expired or is invalid. Please log in again.');
									alertIsShown = true;
								}
								
								sessionStorage.clear();
								localStorage.clear();
								window.location.href = "/";
							} else {
								throw err // an error coming from the backend, for instance 'Alfapass invalid' etc.
							}
						}
					};
				}

				// For non-functions, return as-is
				return Reflect.get(target, prop, receiver);
			}
		};

		// Return a proxy-wrapped version of the service
		return new Proxy(guardedService, handler);
	};

	on = (event, handler) => {
		return window.feathersClient.on(event, handler);
	};

	authenticate = (...args) => {
		return window.feathersClient.authenticate(...args);
	};

	authentication = () => {
		return window.feathersClient.authentication;
	};
}

const client = new Feathers();
export default client;
